import { AnimatePresence, motion } from 'framer-motion'
import { ChevronDown, MessageSquarePlus } from 'lucide-react'
import React from 'react'
import dayjs from 'dayjs'
import { notesVariants, noteItemVariants } from './career-path-constants'
import type { CareerPathNote } from './career-path-types'

interface NotesListProps {
    notes: CareerPathNote[]
    isActive: boolean
    isSelf: boolean
    isExpanded: boolean
    onToggleExpand: () => void
    onAddNote: () => void
    t: (key: string, options?: any) => string
}

const NotesList: React.FC<NotesListProps> = ({
    notes,
    isActive,
    isSelf,
    isExpanded,
    onToggleExpand,
    onAddNote,
    t,
}) => {
    const hasNotes = notes?.length > 0

    // Active stage: always show notes (no collapse)
    if (isActive) {
        return (
            <div className="mt-3">
                {hasNotes && (
                    <div className="space-y-2 border-l-2 border-gray-200 pl-3">
                        {notes.map((note, noteIdx) => (
                            <NoteItem
                                key={note.note_id}
                                note={note}
                                index={noteIdx}
                            />
                        ))}
                    </div>
                )}
                {!isSelf && (
                    <button
                        className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 text-xs text-gray-500 hover:text-blue-600 rounded transition-colors"
                        onClick={onAddNote}
                    >
                        <MessageSquarePlus className="w-3.5 h-3.5" />
                        {t('career_path.add_note')}
                    </button>
                )}
            </div>
        )
    }

    // Completed stage: collapsible notes
    if (!hasNotes) return null

    return (
        <div className="mt-2">
            <button
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
                onClick={onToggleExpand}
            >
                <motion.span
                    animate={{ rotate: isExpanded ? 0 : -90 }}
                    transition={{ duration: 0.2 }}
                >
                    <ChevronDown className="w-3.5 h-3.5" />
                </motion.span>
                {notes.length} {t('career_path.note')}
            </button>

            <AnimatePresence>
                {isExpanded && (
                    <motion.div
                        className="mt-2 space-y-2 border-l-2 border-gray-100 pl-3 overflow-hidden"
                        variants={notesVariants}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                    >
                        {notes.map((note, noteIdx) => (
                            <NoteItem
                                key={note.note_id}
                                note={note}
                                index={noteIdx}
                            />
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}

// --- Individual Note Item ---
const NoteItem: React.FC<{
    note: CareerPathNote
    index: number
}> = ({ note, index }) => (
    <motion.div
        className="flex items-start justify-between gap-2 py-1.5 px-3 rounded bg-gray-50"
        custom={index}
        variants={noteItemVariants}
        initial="hidden"
        animate="visible"
    >
        <div className="flex-1 min-w-0">
            <p className="text-sm text-gray-700">{note.note_content}</p>
            <p className="text-xs text-gray-400 mt-0.5">
                {note.created_by_name} ·{' '}
                {dayjs(note.created_at).format('DD/MM/YYYY')}
            </p>
        </div>
    </motion.div>
)

export default NotesList
